/**
 * Select accessibility projection.
 */
import { projectOverlayOpenerA11y } from "../opener-a11y.js";
import { MDY_WIDGET_CONTRACTS } from "../catalog.js";
import { fieldShellPartIds } from "../field/shell-a11y.js";
import { stateClass } from "../state.js";
const SELECT = MDY_WIDGET_CONTRACTS.select;
export function projectSelectA11y(options) {
    const { widgetId, idFactory, open, activeKey, selectedKey, disabled, readonly, invalid, loading, visibleKeys } = options;
    const { descriptionId, errorId } = fieldShellPartIds(widgetId);
    const describedBy = options.errorsVisible
        ? errorId
        : options.supportingText === true || (options.supportingText ?? "") !== "" ? descriptionId : null;
    const native = options.variant === "native";
    const trigger = {
        id: idFactory.part(widgetId, "trigger"),
        // A `<select>` carries the role already, from being one. Naming it again is at best a repetition
        // and at worst a disagreement with the element underneath.
        ...(native ? {} : { role: "combobox" }),
        classes: buildTriggerClasses(open, disabled, readonly, invalid, loading),
        attributes: {
            ...(native
                ? {}
                : {
                    ...projectOverlayOpenerA11y("select", { widgetId, open, controlsRendered: options.popupRendered ?? true })?.attributes,
                    "aria-activedescendant": activeKey ? idFactory.item(widgetId, "option", activeKey) : undefined,
                }),
            /**
             * The caption, then what the field holds — in that order, and only on the shape that needs it.
             *
             * A `<label for>` names a button, and that is the problem rather than the fix: the computation
             * takes the caption and stops, so the button's own content — which for this control *is* the
             * chosen value — is never appended. A person reaching the field hears what it asks and not
             * what it holds.
             *
             * Two references solve it, the second being the trigger itself: a self-reference in
             * `aria-labelledby` contributes the element's own content, so the name reads "Country, France"
             * without the value needing an id of its own. The `<label for>` stays — it no longer supplies
             * the name, but it is what makes clicking the caption reach the control.
             *
             * The platform's own chooser needs none of this. A `<select>` has a value the reader announces
             * separately, so `for` alone gives "Country, combo box, France", and overriding it would take
             * apart what the platform already does right.
             */
            ...(native
                ? {}
                : { "aria-labelledby": `${fieldShellPartIds(widgetId).labelId} ${idFactory.part(widgetId, "trigger")}` }),
            "aria-invalid": String(invalid),
            ...(options.required === undefined ? {} : { "aria-required": String(options.required) }),
            // The relation every other kind's projection already made, and this one did not: without it a
            // select's errors reach no assistive technology at all.
            "aria-describedby": describedBy,
            // Disabled alone: a read-only control still takes focus and can be copied from.
            //
            // And only where the element does not answer for itself. A `<select>` carries `disabled` as a
            // property the platform already exposes, so an `aria-disabled` beside it is a second answer to
            // a question the element has answered — and two answers is how they come to disagree. The
            // custom trigger is a button standing in for a chooser and has nothing but this to say it.
            ...(native ? {} : { "aria-disabled": String(disabled) }),
            // And read-only said in its own word. The controller refuses the change either way; without
            // this a trigger that will not open its list looks identical to one that will.
            "aria-readonly": readonly ? "true" : null,
            // The native attribute, not only the ARIA. A trigger that says `aria-disabled="true"` and
            // stays clickable is disabled in appearance only — the multiselect trigger has carried this
            // all along, and the two were inconsistent for no reason.
            disabled,
            "data-loading": loading || undefined,
        },
    };
    const search = {
        id: idFactory.part(widgetId, "search"),
        classes: ["mdy-select__search"],
        attributes: {
            role: "combobox",
            "aria-expanded": String(open),
            "aria-controls": idFactory.part(widgetId, "options"),
            "aria-activedescendant": activeKey ? idFactory.item(widgetId, "option", activeKey) : undefined,
            "aria-autocomplete": "list",
        },
    };
    const optionList = {
        id: idFactory.part(widgetId, "options"),
        role: "listbox",
        classes: buildListboxClasses(open),
        attributes: {
            /**
             * Named by the field's caption, never by the control that opens it.
             *
             * `aria-labelledby` pointed at the trigger, and the trigger is a `combobox`. When the name
             * computation crosses into an **embedded control** it takes that control's *value*, not its
             * text — so with nothing chosen the listbox inherited the empty string and was announced as
             * "listbox" and nothing else. The placeholder a person can see inside the button is neither a
             * label nor a value, so it never travelled either.
             *
             * Every attribute in that arrangement was correct and the element pointed at was present and
             * exposed: the defect lived entirely inside the algorithm, which is why nothing that reads
             * markup could see it. It is the same rule as the trigger's self-reference above, read from
             * the other side — there the value is what is wanted, here it is what ruins the name.
             */
            "aria-labelledby": fieldShellPartIds(widgetId).labelId,
            "aria-hidden": String(!open),
        },
    };
    const option = (key, optionDisabled = false) => ({
        id: idFactory.item(widgetId, "option", key),
        role: "option",
        classes: buildOptionClasses(key === selectedKey, key === activeKey, visibleKeys.includes(key), optionDisabled),
        attributes: {
            "aria-selected": String(key === selectedKey),
            // An option a document closed says so before it is pressed. The press is refused either way —
            // the controller will not take it — and an option drawn exactly like an available one invites
            // the press, answers nothing, and explains nothing: a person who cannot see the list reads
            // that as a broken control, and one who can reads it as their own misclick.
            "aria-disabled": String(optionDisabled),
            disabled: optionDisabled,
            // Filtering is the contract's, not the adapter's: an option the query does not match is
            // hidden here, so every renderer filters identically by applying the part.
            hidden: !visibleKeys.includes(key),
        },
    });
    return { trigger, search, options: optionList, option };
}
function buildTriggerClasses(open, disabled, readonly, invalid, loading) {
    // The trigger's own declared states, and only those. A parallel `mdy-control--*` spelling of the
    // same states is not emitted here: no theme styles one, so it would put classes on the trigger
    // that paint nothing.
    // Derived from the declared vocabulary rather than spelled out. `state.ts` exists because a
    // renderer writing `mdy-select__trigger--open` and a theme writing a rule for it agree only by
    // coincidence — and this file was writing eight of them by hand.
    const base = SELECT.parts.trigger.classes[0];
    const states = [
        [open, "open"], [disabled, "disabled"], [readonly, "readonly"],
        [invalid, "invalid"], [loading, "loading"],
    ];
    return [
        ...SELECT.parts.trigger.classes,
        ...states.filter(([on]) => on).map(([, state]) => stateClass(base, state)),
    ];
}
function buildListboxClasses(open) {
    const classes = [...SELECT.parts.options.classes];
    if (open)
        classes.push(...SELECT.parts.options.classes.map((c) => `${c}--open`));
    return classes;
}
function buildOptionClasses(selected, active, visible, disabled) {
    const base = SELECT.parts.option.classes[0] ?? "mdy-select__option";
    const states = [
        [selected, "selected"], [active, "active"], [!visible, "hidden"], [disabled, "disabled"],
    ];
    return [base, ...states.filter(([on]) => on).map(([, state]) => stateClass(base, state))];
}
